import { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Diamond, Gift, CheckCircle } from "@phosphor-icons/react";
import { claimDaily, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const DAY_LABELS = ["Day 1", "Day 2", "Day 3", "Day 4", "Day 5", "Day 6", "Day 7"];

/**
 * DailyBonusDialog — shows a 7-day rotating gem reward grid. Highlights today's
 * unclaimed reward; once the user clicks Claim, gems are credited server-side
 * and onClaimed() is fired so the HUD reflects the new balance.
 *
 * Props:
 *   open, onOpenChange      — controlled visibility
 *   status                  — { claimable, current_streak, next_streak_day, next_reward, schedule, last_claim }
 *   onClaimed({ gems, streak_day, current_gems })
 */
export default function DailyBonusDialog({ open, onOpenChange, status, onClaimed }) {
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [lastReward, setLastReward] = useState(0);

  useEffect(() => {
    if (open) {
      setClaimed(false);
      setLastReward(0);
    }
  }, [open]);

  const handleClaim = useCallback(async () => {
    if (claiming || claimed) return;
    setClaiming(true);
    try {
      const r = await claimDaily();
      setClaimed(true);
      setLastReward(r.gems_awarded);
      onClaimed?.({
        gems_awarded: r.gems_awarded,
        streak_day: r.streak_day,
        current_gems: r.current_gems,
      });
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setClaiming(false);
    }
  }, [claiming, claimed, onClaimed]);

  if (!status) return null;
  const schedule = status.schedule || [];
  const nextDay = status.next_streak_day || 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="daily-bonus-dialog"
        className="rounded-none border border-cyan-400/30 bg-[#0A0E14] text-white max-w-xl p-0"
      >
        <DialogHeader className="px-5 py-4 border-b border-white/10">
          <DialogTitle className="mono uppercase tracking-widest text-[12px] text-cyan-300 flex items-center gap-2">
            <Gift size={16} weight="fill" /> Daily Login Bonus
          </DialogTitle>
          <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
            Current streak:{" "}
            <span className="text-cyan-300">{status.current_streak} day{status.current_streak === 1 ? "" : "s"}</span>
            <span className="mx-2 text-zinc-700">·</span>
            Today is Day {nextDay} of 7
          </div>
        </DialogHeader>

        <div className="px-5 py-5">
          <div className="grid grid-cols-7 gap-2 mb-5">
            {schedule.map((gems, idx) => {
              const day = idx + 1;
              const isToday = day === nextDay;
              const isPast = day < nextDay;
              return (
                <div
                  key={day}
                  data-testid={`daily-day-${day}`}
                  className={`flex flex-col items-center border p-2 ${
                    isToday
                      ? (claimed
                          ? "border-emerald-500/60 bg-emerald-500/10"
                          : "border-cyan-400 bg-cyan-400/10 shadow-[0_0_16px_rgba(34,211,238,0.25)]")
                      : isPast
                      ? "border-white/5 bg-white/[0.02] opacity-50"
                      : "border-white/10 bg-white/[0.02]"
                  }`}
                >
                  <span className="mono text-[8px] uppercase tracking-widest text-zinc-500">
                    {DAY_LABELS[idx]}
                  </span>
                  <div className="my-1 flex items-center gap-0.5">
                    <Diamond
                      size={12}
                      weight="fill"
                      className={isToday && claimed ? "text-emerald-300" : "text-cyan-300"}
                    />
                    <span className={`mono font-bold text-sm ${
                      isToday && claimed ? "text-emerald-300" : isToday ? "text-cyan-200" : "text-zinc-300"
                    }`}>
                      {gems}
                    </span>
                  </div>
                  {isToday && claimed && (
                    <CheckCircle size={12} weight="fill" className="text-emerald-400" />
                  )}
                </div>
              );
            })}
          </div>

          {claimed ? (
            <div className="border border-emerald-500/30 bg-emerald-500/5 p-4 text-center">
              <div className="mono text-[10px] uppercase tracking-widest text-emerald-400 mb-1">
                ✓ Claimed
              </div>
              <div className="text-xl font-bold text-emerald-300 flex items-center justify-center gap-1.5">
                <Diamond size={18} weight="fill" /> +{lastReward}
              </div>
              <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mt-2">
                Come back tomorrow for{" "}
                <span className="text-cyan-300">
                  {schedule[(nextDay) % 7] || schedule[0]} gems
                </span>
              </div>
            </div>
          ) : status.claimable ? (
            <Button
              data-testid="daily-claim-btn"
              disabled={claiming}
              onClick={handleClaim}
              className="w-full rounded-none h-11 mono uppercase text-[12px] tracking-widest bg-cyan-400 text-black hover:bg-cyan-300 disabled:opacity-50"
            >
              <Gift size={14} className="mr-2" weight="fill" />
              {claiming ? "Claiming…" : `Claim ${status.next_reward} Gems`}
            </Button>
          ) : (
            <div className="border border-white/10 bg-white/[0.02] p-3 text-center">
              <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">
                Already claimed today
              </div>
              <div className="mono text-[10px] uppercase tracking-widest text-cyan-300 mt-1">
                Next reward: {status.next_reward} gems
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
